import { useCallback, useEffect, useState, type FormEvent } from "react";

import {
  EMPTY_ORG_PROFILE,
  fetchOrgProfile,
  REQUIRED_ORG_FIELDS,
  saveOrgProfile,
  type OrgProfile,
} from "../lib/orgProfile";

/**
 * Секция «Настроек» — реквизиты организации-владельца инстанса (E15, Р-14).
 *
 * Откуда это берётся и зачем: реквизиты печатаются в ШАПКЕ расчёта неустойки (E9a) — документа,
 * который прикладывается к претензии и уходит контрагенту, а при споре в суд. До E15 они жили в
 * конфигурации сервера, и смена наименования требовала правки настроек VM и перезапуска. Теперь
 * их ведёт администратор системы, и изменение применяется сразу.
 *
 * Только администратор: сервер требует право `settings:manage` на каждом маршруте; видимость
 * раздела это лишь дублирует (правило 5). Оформление — токены docs/07, тексты русские.
 */

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";
const labelCls = "mb-1 block text-[12px] font-bold text-muted";

type Load =
  { kind: "loading" } | { kind: "ready"; org: OrgProfile } | { kind: "error"; message: string };

interface FieldSpec {
  readonly key: keyof OrgProfile;
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  /** Числовой реквизит — моноширинный ввод и цифровая клавиатура. */
  readonly numeric?: boolean;
  readonly wide?: boolean;
}

const FIELDS: readonly FieldSpec[] = [
  {
    key: "name",
    id: "org-name",
    label: "Полное наименование",
    hint: "Как в уставе — эта строка печатается в шапке расчёта.",
    wide: true,
  },
  { key: "inn", id: "org-inn", label: "ИНН", hint: "10 или 12 цифр.", numeric: true },
  { key: "kpp", id: "org-kpp", label: "КПП", hint: "9 цифр; у ИП его нет.", numeric: true },
  { key: "ogrn", id: "org-ogrn", label: "ОГРН", hint: "13 или 15 цифр.", numeric: true },
  {
    key: "address",
    id: "org-address",
    label: "Адрес",
    hint: "Одной строкой — по нему контрагент направляет ответ на претензию.",
    wide: true,
  },
  { key: "phone", id: "org-phone", label: "Телефон" },
  { key: "email", id: "org-email", label: "Электронная почта" },
];

/** Русские названия обязательных реквизитов — те же, что называет печатная форма. */
const REQUIRED_LABELS: Partial<Record<keyof OrgProfile, string>> = {
  name: "наименование",
  inn: "ИНН",
  address: "адрес",
};

export function OrgProfileSection() {
  const [state, setState] = useState<Load>({ kind: "loading" });
  const [draft, setDraft] = useState<OrgProfile>(EMPTY_ORG_PROFILE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchOrgProfile();
    if (!res.ok) {
      setState({ kind: "error", message: res.message });
      return;
    }
    setState({ kind: "ready", org: res.value });
    setDraft(res.value);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    setBusy(true);
    const res = await saveOrgProfile(draft);
    setBusy(false);
    if (!res.ok) {
      // Текст отказа даёт сервер: формат ИНН/КПП/ОГРН он проверяет сам, и вторая проверка
      // на клиенте рано или поздно разошлась бы с ней.
      setError(res.message);
      return;
    }
    setState({ kind: "ready", org: res.value });
    setDraft(res.value);
    setSaved(true);
  };

  // Незаполненные ОБЯЗАТЕЛЬНЫЕ реквизиты. Сохранению не мешают — предупреждаем, но не запрещаем:
  // администратор может заполнять карточку в несколько заходов, а вписанное «лишь бы сохранить»
  // уедет в документ и станет ошибкой в претензии.
  const missing = REQUIRED_ORG_FIELDS.filter((k) => draft[k].trim() === "").map(
    (k) => REQUIRED_LABELS[k] ?? k,
  );

  return (
    <section aria-labelledby="org-title" className="mt-10">
      <h2 id="org-title" className="mb-1 text-[16px] font-bold text-ink">
        Организация
      </h2>
      <p className="mb-3 text-[13px] text-muted">
        Реквизиты вашей организации. Они печатаются в шапке расчёта неустойки, который
        прикладывается к претензии, — поэтому вносите их так, как они указаны в уставе и выписке.
        Изменения применяются сразу и записываются в журнал аудита.
      </p>

      {state.kind === "loading" && (
        <p className="text-[13px] text-muted" role="status">
          Загрузка реквизитов…
        </p>
      )}

      {state.kind === "error" && (
        <p role="alert" className="text-[13px] text-overdue-fg">
          {state.message}
        </p>
      )}

      {state.kind === "ready" && (
        <form onSubmit={(e) => void onSubmit(e)} className="max-w-[var(--w-form)]">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <div key={f.key} className={f.wide ? "sm:col-span-2" : undefined}>
                <label htmlFor={f.id} className={labelCls}>
                  {f.label}
                </label>
                <input
                  id={f.id}
                  value={draft[f.key]}
                  inputMode={f.numeric ? "numeric" : undefined}
                  aria-describedby={f.hint ? `${f.id}-hint` : undefined}
                  onChange={(e) => {
                    setSaved(false);
                    setError(null);
                    setDraft((prev) => ({ ...prev, [f.key]: e.target.value }));
                  }}
                  className={`${inputCls} ${f.numeric ? "num" : ""}`}
                />
                {f.hint && (
                  <p id={`${f.id}-hint`} className="mt-1 text-[11px] text-muted">
                    {f.hint}
                  </p>
                )}
              </div>
            ))}
          </div>

          {missing.length > 0 && (
            <p role="status" className="mt-3 text-[13px] text-soon-fg">
              Не заполнено: {missing.join(", ")}. Сохранить можно, но в печатной форме расчёта на
              месте этих реквизитов будет отметка о том, что они не заданы.
            </p>
          )}

          {error && (
            <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
              {error}
            </p>
          )}
          {saved && (
            <p role="status" className="mt-3 text-[13px] text-done-fg">
              Реквизиты сохранены.
            </p>
          )}

          <div className="mt-4">
            <button type="submit" disabled={busy} className={btnPrimary}>
              {busy ? "Сохранение…" : "Сохранить реквизиты"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
